import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router';
import { usersApi, tenantsApi } from '@/lib/api';
import { BaseForm, type FieldConfig } from '@/components/custom/base-form';
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@/config/constants';
import type { UserCreateInput, UserUpdateInput } from '@/types/entities';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

type Props = {
  type: 'create' | 'edit';
};

export function AdminUsersForm({ type }: Props) {
  const { id } = useParams();
  const navigate = useNavigate();

  const formSchema = type === 'edit'
    ? z.object({
        email: z.email('Please enter a valid email address'),
        display_name: z.string().min(1, 'Display name is required'),
        password: z.string().optional().refine(
          (val) =>
            !val ||
            (val.length >= MIN_PASSWORD_LENGTH && val.length <= MAX_PASSWORD_LENGTH),
          {
            message: `Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters`,
          }
        ),
      })
    : z.object({
        email: z.string().email('Please enter a valid email address'),
        display_name: z.string().min(1, 'Display name is required'),
        tenant_slug: z.string().min(1, 'Tenant is required'),
        password: z
          .string()
          .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
          .max(MAX_PASSWORD_LENGTH, `Password must be at most ${MAX_PASSWORD_LENGTH} characters`),
      });

  const [defaultValues, setDefaultValues] = useState<Partial<z.infer<typeof formSchema>>>({
    email: "",
    display_name: "",
    password: "",
    ...(type === 'create' ? { tenant_slug: "" } : {}),
  });

  const [tenants, setTenants] = useState<Array<{ id: number; name: string; slug: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    async function fetchData() {
      try {
        // Always fetch tenants for the selector (needed in create mode)
        const tenantsData = await tenantsApi.getAll();
        setTenants(tenantsData);

        if (type === 'edit' && id) {
          const user = await usersApi.getById(Number(id));
          setDefaultValues({
            email: user.email || "",
            display_name: user.display_name || "",
            password: "",
          });
        } else {
          setDefaultValues({
            email: "",
            display_name: "",
            password: "",
            tenant_slug: "",
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [type, id]);

  const tenantOptions = tenants.map(tenant => ({
    label: tenant.name,
    value: tenant.slug,
  }));

  const fields: FieldConfig[] = [
    ...(type === 'create' ? [{
      name: "tenant_slug",
      label: "Tenant",
      type: "select" as const,
      options: tenantOptions,
      placeholder: "Select a tenant",
    }] : []),
    {
      name: "email",
      label: "Email",
      type: "email",
      placeholder: "user@example.com",
    },
    {
      name: "display_name",
      label: "Display Name",
      type: "text",
      placeholder: "John Doe",
    },
    {
      name: "password",
      label: `Password${type !== "edit" ? " *" : ""}`,
      type: "password",
      placeholder: type === "edit" ? "Leave empty to keep current" : "••••••••",
    },
  ];

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    setError(null);

    setIsSubmitting(true);
    try {
      if (type === 'edit' && id) {
        const editValues = values as { email: string; display_name: string; password?: string };
        const data: UserUpdateInput = {
          email: editValues.email.trim(),
          display_name: editValues.display_name.trim(),
          ...(editValues.password ? { password: editValues.password.trim() } : {}),
        };
        await usersApi.update(Number(id), data);
        toast.success('User updated successfully');
      } else {
        const createValues = values as { email: string; display_name: string; tenant_slug: string; password: string };
        const data: UserCreateInput = {
          tenant_slug: createValues.tenant_slug,
          email: createValues.email.trim(),
          display_name: createValues.display_name.trim(),
          password: createValues.password.trim(),
        };
        await usersApi.create(data);
        toast.success('User created successfully');
      }
      navigate('/admin/dashboard/users');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const notes = type !== "edit"
    ? `Password must be between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH} characters`
    : "Leave \"Password\" blank to keep the current password.";

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <PageHeader
        title={type === 'edit' ? 'Edit User' : 'Create User'}
        description={type === 'edit' ? 'Update details for this user.' : 'Create a new user below.'}
      />

      <PageSection>
        {isLoading && (
          <div className="flex items-center justify-center rounded-xl border border-border bg-card p-8 text-muted-foreground">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Loading user data...
          </div>
        )}
        {!isLoading && (
          <>
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}
            <BaseForm
              schema={formSchema}
              fields={fields}
              defaultValues={defaultValues}
              onSubmit={handleSubmit}
              submitLabel={
                isSubmitting
                  ? "Saving..."
                  : type === "edit"
                    ? "Save Changes"
                    : "Create"
              }
              notes={notes}
              isLoading={isSubmitting}
              onCancel={() => navigate(-1)}
            />
          </>
        )}
      </PageSection>
    </div>
  );
}
